import React from "react";
import { LucideIcon } from "lucide-react";
import { CampaignItem } from "./index";
import { ProfileCardActions } from "./profile-card-actions";

interface ProfileCardItem {
  icon: LucideIcon;
  label: string;
  value: string | number | null | undefined;
  nullValue?: boolean;
}

interface ProfileCardBaseProps {
  name: string;
  logoUrl?: string;
  fallbackIcon: LucideIcon;
  items: ProfileCardItem[];
  bannerUrl?: string;
  profileId: string;
  profileType: string;
  userId?: number;
  entity: CampaignItem;
}

const BG_FALLBACK_STYLE = {
  backgroundColor: "#e5e5f7",
  opacity: 0.2,
  background:
    "radial-gradient(circle, transparent 20%, #e5e5f7 20%, #e5e5f7 80%, transparent 80%, transparent), radial-gradient(circle, transparent 20%, #e5e5f7 20%, #e5e5f7 80%, transparent 80%, transparent) 25px 25px, linear-gradient(#444cf7 2px, transparent 2px) 0 -1px, linear-gradient(90deg, #444cf7 2px, #e5e5f7 2px) -1px 0",
  backgroundSize: "50px 50px, 50px 50px, 25px 25px, 25px 25px",
};

export function ProfileCardBase({
  name,
  logoUrl,
  fallbackIcon: FallbackIcon,
  items,
  bannerUrl,
  userId,
  entity,
}: ProfileCardBaseProps) {
  return (
    <div className="card rounded-lg shadow-md ring-1 ring-black/5 bg-white hover:shadow-xl overflow-hidden p-0">
      <div className="flex flex-col items-center h-full justify-center relative group overflow-hidden divide-y divide-gray-300">
        {/* Banner */}
        {bannerUrl ? (
          <div
            className="overflow-hidden z-[1] w-full h-[200px]"
            style={{
              backgroundImage: `url(${bannerUrl})`,
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}
          ></div>
        ) : (
          <div
            className="overflow-hidden z-[1] w-full h-[200px]"
            style={BG_FALLBACK_STYLE}
          ></div>
        )}

        <div className="flex flex-col justify-between space-betwen h-full relative z-[2] w-full">
          <div className="flex flex-col items-center justify-between">
            {/* Logo */}
            <div className="-mt-12 w-[120px] h-[120px] lg:w-20 lg:h-20 bg-white border border-default-200 rounded-lg flex items-center p-2 flex items-center justify-center">
              {logoUrl ? (
                <img src={logoUrl} alt={name} />
              ) : (
                <FallbackIcon className="w-16 h-16 text-default-200" />
              )}
            </div>

            {/* Title */}
            <div className="p-4 pb-0 flex flex-col space-y-2">
              <h3 className="text-default-600 text-center">
                <span className="font-bold">{name}</span>
              </h3>
            </div>
          </div>

          {/* Details */}
          <div className="divide-y text-xs font-light text-slate-500 w-full p-4 pt-0 pb-2">
            {items.map((item, index) => (
              <ProfileCardItemRow key={index} {...item} />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="w-full px-4 pb-4">
            <ProfileCardActions userId={userId} entity={entity} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileCardItemRow({
  icon: Icon,
  label,
  value,
  nullValue,
}: ProfileCardItem) {
  const isNull = nullValue ?? !value;
  const displayValue = isNull ? "N.C." : value;

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center space-x-2">
        <Icon className="w-3 h-3" />
        <span className="truncate font-medium">{label}</span>
      </div>
      <div className={`truncate ${isNull ? "opacity-50" : ""}`}>
        {displayValue}
      </div>
    </div>
  );
}
